// @flow
import { alerts as alertConstants, feedback as feedbackConstants } from '~/core/constants';
import { call, cancel, fork, put, select, take, takeEvery, takeLatest } from 'redux-saga/effects';
import { delay } from 'redux-saga';
import { getLastActivity, getUserId } from './selectors';
import { logInComplete, logOut } from '~/core/state/application/actions';
import { analyticsSharingToggled, pinSet, settingPin } from './actions';
import { SET_PIN, TOGGLE_ANALYTICS_SHARING_ALLOWED } from './constants';
import { getConfig } from '~/core/state/config/selectors';
import handleApi from '~/core/state/handle-api';
import handleSaga from '~/core/state/handle-saga';
import type { Effect } from 'redux-saga';
import messages from './intl';
import moment from 'moment-timezone';
import { noop } from 'lodash/fp';
import { requestAlert } from '~/core/state/alerts/actions';
import { user as userApi } from '~/core/api';

export const timestampNow: () => moment = () => moment().valueOf();

export function* isUserStillActiveSaga(): Generator<Effect, *, *> {
    const inactiveTimeout = yield select(getConfig, 'user:inactiveTimeout');

    while (true) {
        yield call(delay, inactiveTimeout / 4);

        const lastActivity = yield select(getLastActivity);
        const currentTime = yield call(timestampNow);
        const millisecondsSinceLastActive = currentTime - lastActivity;

        if (millisecondsSinceLastActive >= inactiveTimeout) {
            return yield put(logOut());
        }
    }
}

function* userStillActiveDaemonSaga(): Generator<Effect, *, *> {
    yield take(logInComplete);
    const daemon = yield fork(isUserStillActiveSaga);
    yield take(logOut);
    yield cancel(daemon);
}

export function* setPinSaga({
    payload: { confirm, pin }
}: {
    payload: {
        confirm: string,
        pin: string
    }
}): Generator<noop | Effect, *, *> {
    if (pin !== confirm) {
        yield put(settingPin(new Error('Pins not matching')));
        return yield put(requestAlert(messages.notMatching, alertConstants.types.ERROR));
    }

    yield put(settingPin());

    const userId = yield select(getUserId);
    const [, error] = yield call(handleApi(userApi.setPin), userId, pin);

    if (error) {
        return yield put(settingPin(error));
    }

    yield put(pinSet());
}

function* watchSetPin(): Generator<Effect, *, *> {
    yield takeLatest(SET_PIN, handleSaga(setPinSaga));
}

export function* toggleAnalyticsSharingAllowedSaga({
    payload: { allowAnalyticsSharing }
}: {
    payload: { allowAnalyticsSharing: boolean }
}): Generator<Effect, *, *> {
    const userId = yield select(getUserId);
    const [, error] = yield call(handleApi(userApi.setAllowAnalyticsSharing), userId, allowAnalyticsSharing);

    if (error) {
        return yield put(requestAlert(feedbackConstants.generic.UNEXPECTED_ERROR, alertConstants.types.ERROR));
    }

    yield put(analyticsSharingToggled());
}

function* watchToggleAnalyticsSharingAllowed(): Generator<Effect, *, *> {
    yield takeEvery(TOGGLE_ANALYTICS_SHARING_ALLOWED, handleSaga(toggleAnalyticsSharingAllowedSaga));
}

export default [userStillActiveDaemonSaga, watchSetPin, watchToggleAnalyticsSharingAllowed];



// WEBPACK FOOTER //
// ./src/core/state/user/sagas.js